import React, { useMemo } from 'react';
import { Box, ButtonBase, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import {
  KeyboardArrowDown as ExpandedIcon,
  KeyboardArrowRight as CollapsedIcon,
  SupervisorAccount as ManagerIcon,
} from '@mui/icons-material';
import { formatHours } from './format';
import { STATUS_STYLE } from './completeness';
import { cellBase, FIRST_COL, stickyLeft } from './tableStyles';
import MonthGrid, { measureDayColumns, measureRightColumn, rightSx } from './MonthGrid';

const NAME_COL = FIRST_COL + 40;
const INDENT_PX = 18;
// Zona cliccabile per aprire/chiudere i riporti: quadrata, alta quanto la riga
const TOGGLE_SIZE = 34;
const PROJECT_BG = 'var(--rt-row-sub)';
const OPEN_BG = 'var(--rt-row-open)';
const RIGHT_COLUMNS = [
  { key: 'missing', label: 'Missing', color: 'var(--rt-danger)' },
  { key: 'total', label: 'Tot (h)' },
];

function cellContent(s) {
  if (s.status === 'absence' && !s.minutes) return <span style={{ fontSize: 10 }}>OFF</span>;
  if (s.status === 'missing') return '0';
  if (!s.minutes) return '';
  return formatHours(s.minutes);
}

function PersonRow({ row, monthDays, totalWidth, onToggleTeam, onToggleProjects }) {
  const { person, summary, depth, hasReports, teamOpen, projectsOpen, teamStats, managerName } = row;
  const rowBg = projectsOpen ? OPEN_BG : 'background.paper';
  return (
    <TableRow
      hover
      onClick={() => onToggleProjects(person.id)}
      sx={{ cursor: 'pointer', '&:hover td': { filter: 'brightness(0.97)' } }}
    >
      <TableCell sx={{ ...cellBase, ...stickyLeft, bgcolor: rowBg, textAlign: 'left', pr: 1, pl: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, height: TOGGLE_SIZE }}>
          {hasReports ? (
            <Tooltip title={teamOpen ? 'Hide reports' : 'Show reports'}>
              <ButtonBase
                aria-label={teamOpen ? 'Hide reports' : 'Show reports'}
                aria-expanded={teamOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleTeam(person.id);
                }}
                sx={{
                  // Copre tutta l'area a sinistra del nome, rientro compreso
                  width: depth * INDENT_PX + TOGGLE_SIZE,
                  pl: `${depth * INDENT_PX}px`,
                  height: TOGGLE_SIZE,
                  flexShrink: 0,
                  borderRadius: 0,
                  color: teamOpen ? 'success.main' : 'text.secondary',
                  '&:hover': { bgcolor: 'rgba(90,176,49,0.14)' },
                }}
              >
                {teamOpen ? <ExpandedIcon fontSize="small" /> : <CollapsedIcon fontSize="small" />}
              </ButtonBase>
            </Tooltip>
          ) : (
            <Box sx={{ width: depth * INDENT_PX + TOGGLE_SIZE, flexShrink: 0 }} />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title="Click to see hours by project" enterDelay={700} placement="top-start">
              <Typography variant="body2" noWrap sx={{ fontSize: 13, fontWeight: person.isPeopleManager ? 700 : 500 }}>
                {person.name}
              </Typography>
            </Tooltip>
            {(managerName || teamStats) && (
              <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ lineHeight: 1.1, mt: -0.25 }}>
                {teamStats ? (
                  <>
                    <ManagerIcon sx={{ fontSize: 12, verticalAlign: '-2px', mr: 0.25 }} />
                    {teamStats.count} {teamStats.count === 1 ? 'person' : 'people'}
                    {teamStats.incomplete > 0 && <span style={{ color: 'var(--rt-danger)' }}> · {teamStats.incomplete} incomplete</span>}
                  </>
                ) : (
                  managerName
                )}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      {summary.statuses.map((s, i) => {
        const style = STATUS_STYLE[s.status];
        const d = monthDays[i];
        let title = style.title || '';
        if (s.status === 'under' || s.status === 'missing') {
          title = `${formatHours(s.minutes)}h of ${formatHours(s.target)}h`;
        }
        if (s.status === 'future' && s.minutes) title = 'Forecast';
        return (
          <TableCell
            key={d.day}
            title={title}
            sx={{
              ...cellBase,
              bgcolor: style.bg || (projectsOpen ? OPEN_BG : 'background.paper'),
              color: style.color,
              fontWeight: s.status === 'under' || s.status === 'missing' ? 700 : 400,
              fontStyle: s.status === 'future' ? 'italic' : 'normal',
              boxShadow: d.today ? 'inset 0 0 0 1px var(--rt-today-outline)' : 'none',
            }}
          >
            {cellContent(s)}
          </TableCell>
        );
      })}
      <TableCell
        sx={{
          ...cellBase,
          ...rightSx(0, 2, totalWidth),
          bgcolor: rowBg,
          fontWeight: 700,
          color: summary.missing ? 'var(--rt-danger)' : 'text.disabled',
        }}
      >
        {summary.missing ? formatHours(summary.missing) : '—'}
      </TableCell>
      <TableCell sx={{ ...cellBase, ...rightSx(1, 2, totalWidth), bgcolor: rowBg, fontWeight: 700 }}>{formatHours(summary.total)}</TableCell>
    </TableRow>
  );
}

function ProjectRows({ row, monthDays, totalWidth }) {
  const projects = Object.values(row.projects || {}).sort((a, b) => a.name.localeCompare(b.name));
  const indent = row.depth * INDENT_PX + TOGGLE_SIZE + 4;
  if (!projects.length) {
    return (
      <TableRow>
        <TableCell colSpan={monthDays.length + 3} sx={{ ...cellBase, textAlign: 'left', bgcolor: PROJECT_BG }}>
          <Typography variant="body2" color="text.secondary" sx={{ position: 'sticky', left: 0, display: 'inline-block', pl: `${indent}px` }}>
            No hours tracked this month.
          </Typography>
        </TableCell>
      </TableRow>
    );
  }
  return projects.map((p) => {
    const total = Object.values(p.days).reduce((sum, c) => sum + c.actual, 0);
    return (
      <TableRow key={p.name}>
        <TableCell sx={{ ...cellBase, ...stickyLeft, bgcolor: PROJECT_BG, textAlign: 'left', pr: 1, pl: `${indent}px` }}>
          <Typography variant="body2" noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
            {p.name}
          </Typography>
        </TableCell>
        {monthDays.map((d) => {
          const c = p.days[d.day];
          const minutes = d.future ? c?.forecast : c?.actual;
          return (
            <TableCell
              key={d.day}
              sx={{
                ...cellBase,
                bgcolor: d.weekend ? 'var(--rt-weekend)' : PROJECT_BG,
                color: d.future ? 'var(--rt-forecast)' : 'text.secondary',
                fontStyle: d.future ? 'italic' : 'normal',
              }}
            >
              {minutes ? formatHours(minutes) : ''}
            </TableCell>
          );
        })}
        <TableCell sx={{ ...cellBase, ...rightSx(0, 2, totalWidth), bgcolor: PROJECT_BG }} />
        <TableCell sx={{ ...cellBase, ...rightSx(1, 2, totalWidth), bgcolor: PROJECT_BG, color: 'text.secondary', fontWeight: 600 }}>
          {formatHours(total)}
        </TableCell>
      </TableRow>
    );
  });
}

/**
 * Tabella mese per persone: righe ad albero per gerarchia (o elenco piatto),
 * stato di completezza per giorno, ore mancanti e dettaglio per progetto.
 */
export default function PeoplePivotTable({ monthDays, rows, loading, onToggleTeam, onToggleProjects, incompleteByDay, dayTotals, totals }) {
  // Colonne giorno larghe quanto il valore più lungo (ore, forecast, totali)
  const dayWidths = useMemo(
    () =>
      measureDayColumns(monthDays, (consider) => {
        rows.forEach((r) => {
          if (r.type !== 'person') return;
          r.summary.statuses.forEach((s, i) => consider(monthDays[i].day, s.minutes ? formatHours(s.minutes) : ''));
          if (r.projectsOpen) {
            Object.values(r.projects || {}).forEach((p) =>
              Object.entries(p.days).forEach(([day, c]) => consider(Number(day), formatHours(c.actual || c.forecast))),
            );
          }
        });
        dayTotals.forEach((m, i) => consider(monthDays[i].day, m ? formatHours(m) : ''));
      }),
    [rows, monthDays, dayTotals],
  );
  const totalWidth = measureRightColumn(formatHours(totals.total));

  return (
    <MonthGrid
      monthDays={monthDays}
      firstColLabel="Person"
      firstColWidth={NAME_COL}
      dayWidths={dayWidths}
      rightColumns={RIGHT_COLUMNS}
      rightWidth={totalWidth}
      loading={loading}
      skeletonRows={6}
      fillDeps={[rows.length > 0]}
      dayHeaderExtra={(d, i) => (
        <Tooltip title={incompleteByDay[i] ? `${incompleteByDay[i]} incomplete` : ''}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--rt-danger)', minHeight: 13 }}>{incompleteByDay[i] || ''}</div>
        </Tooltip>
      )}
      totalRow={
        rows.length
          ? {
              cell: (d, i) => ({
                content: dayTotals[i] ? formatHours(dayTotals[i]) : '',
                sx: { color: d.future ? 'var(--rt-forecast)' : 'text.primary', fontStyle: d.future ? 'italic' : 'normal' },
              }),
              right: [
                { content: totals.missing ? formatHours(totals.missing) : '—', sx: { color: 'var(--rt-danger)' } },
                { content: formatHours(totals.total) },
              ],
            }
          : null
      }
    >
      {rows.map((row) =>
        row.type === 'person' ? (
          <React.Fragment key={`p-${row.person.id}`}>
            <PersonRow row={row} monthDays={monthDays} totalWidth={totalWidth} onToggleTeam={onToggleTeam} onToggleProjects={onToggleProjects} />
            {row.projectsOpen && <ProjectRows row={row} monthDays={monthDays} totalWidth={totalWidth} />}
          </React.Fragment>
        ) : null,
      )}
    </MonthGrid>
  );
}
